import Vue from "vue"
import Resource from "@/api/providers/Resource"
const ApiUrl = process.env.VUE_APP_API

// Removes codelists that are already loaded from params
function removeExistingCodelistKeysFromParams (params, codeLists) {
  let loadedCodelists = Object.keys(codeLists)
  if (typeof params === "string" && loadedCodelists.includes(params)) return ""
  // If params aren't a string they should be an array
  params.forEach((e, key) => {
    let currentCodelistLoaded = loadedCodelists.includes(e)
    if (currentCodelistLoaded) params.splice(params.indexOf(e), 1)
  })
  return params
}

export default class CodeLists extends Resource {
    route = "codelists"

    state = {
      multipleLoadersTypes: ["updateData", "removeData"],
      loaders: {
        list: false,
        details: false,
        store: false,
        update: [],
        remove: [],
        storeData: false,
        updateData: [],
        removeData: []
      },
      // Full codelist namespaced by schema slug (schema object)
      codeLists: {},
      // Codelist data namespaced by schema slug (array of objects)
      options: {},
      // Codelist data namespaced by schema slug and data id (object with number key and object value)
      optionsById: {},
      // Codelist data namespaced by schema slug and data key (object with string key and object value)
      optionsByKey: {},
      // Codelist schema key to id map
      schemaIds: {},
      // Codelist schema id to key map
      schemaKeys: {}
    }

    actions = {
      /**
     * Gets a single or multiple codelists and their data.
     * If a codelist is already loaded, it won't be refetched
     * unless the overwrite param is set to true
     *
     * @param {number|array} params - A single or multiple slugs of wanted codelists
     * @param {boolean} overwrite - Should the loaded codelists be overwriten
     *
     * @returns {object} Response status and data
     */
      async get ({ state, commit }, { params, overwrite = false }) {
        if (!overwrite) params = removeExistingCodelistKeysFromParams(params, state.codeLists)
        if (params.length === 0) return
        commit("SET_LOADER", { type: "get", value: true })
        try {
          var promise = await window.axios.get(`${ApiUrl}${state.route}/get/${params}`)
        } catch (error) {
          commit("SET_LOADER", { type: "get", value: false })
          let errors = {}
          Object.keys(error.response.data.errors).forEach(e => { errors[e] = error.response.data.errors[e][0] })
          commit("SET_ERRORS", errors)
          return { ...error.response }
        }
        try {
          if (Array.isArray(params)) {
            params.forEach(param => {
              if (!promise.data[param]) return
              commit("SET_CODELIST_OPTIONS", { slug: param, codeList: promise.data[param] })
            })
          } else {
            commit("SET_CODELIST_OPTIONS", { slug: params, codeList: promise.data })
          }
        } catch {}
        commit("SET_LOADER", { type: "get", value: false })
        return promise
      },
      /**
     * Deletes a codelistSchema
     *
     * @param {number} id - Id of the schema we wish to delete
     *
     * @returns {object} Response status and data
     */
      async remove ({ rootState, state, commit }, id) {
        try {
          commit("SET_LOADER", { type: "remove", key: id, value: true })
          let promise = await window.axios.delete(`${rootState.apiUrl}${state.route}/${id}`)
          commit("REMOVE_CODELIST_OPTIONS", { id })
          commit("SET_LOADER", { type: "remove", key: id, value: false })
          return { status: promise.status, data: promise.data }
        } catch (error) {
          commit("SET_LOADER", { type: "remove", key: id, value: false })
          return { ...error.response }
        }
      },
      /**
       * Updates a codelist data record
       *
       * @param {number} schema_slug - Slug of the codelist schema
       * @param {number} data_id - Id of the codelist data
       * @param {object} params - Params we wish to update the record with
       *
       * @returns {object} Response status and data
       */
      // eslint-disable-next-line camelcase
      async updateData ({ rootState, state, commit }, { schema_slug, data_id, params }) {
        try {
          commit("SET_ERRORS", {})
          commit("SET_LOADER", { type: "updateData", key: data_id, value: true })
          let promise = await window.axios.put(`${rootState.apiUrl}${state.route}/updateData`, { schema_slug, data_id, params })
          commit("SET_LOADER", { type: "updateData", key: data_id, value: false })
          return { status: promise.status, data: promise.data }
        } catch (error) {
          commit("SET_LOADER", { type: "updateData", key: data_id, value: false })
          let errors = {}
          Object.keys(error.response.data.errors).forEach(e => { errors[e] = error.response.data.errors[e][0] })
          commit("SET_ERRORS", errors)
          return { ...error.response }
        }
      },
      /**
       * Stores a codelist data record
       *
       * @param {number} schema_slug - Slug of the codelist schema
       * @param {object} params - Params we wish to store
       *
       * @returns {object} Response status and data
       */
      // eslint-disable-next-line camelcase
      async storeData ({ rootState, state, commit }, { schema_slug, params }) {
        try {
          commit("SET_ERRORS", {})
          commit("SET_LOADER", { type: "storeData", value: true })
          let promise = await window.axios.post(`${rootState.apiUrl}${state.route}/storeData`, { schema_slug, params })
          commit("SET_LOADER", { type: "storeData", value: false })
          return { status: promise.status, data: promise.data }
        } catch (error) {
          commit("SET_LOADER", { type: "storeData", value: false })
          let errors = {}
          Object.keys(error.response.data.errors).forEach(e => { errors[e] = error.response.data.errors[e][0] })
          commit("SET_ERRORS", errors)
          return { ...error.response }
        }
      },
      /**
       * Removes a codelist data record
       *
       * @param {number} schema_slug - Slug of the codelist schema
       * @param {number} data_id - Id of the codelist data
       *
       * @returns {object} Response status and data
       */
      // eslint-disable-next-line camelcase
      async removeData ({ rootState, state, commit }, { schema_slug, data_id }) {
        try {
          commit("SET_ERRORS", {})
          commit("SET_LOADER", { type: "removeData", key: data_id, value: true })
          let promise = await window.axios.post(`${rootState.apiUrl}${state.route}/removeData`, { schema_slug, data_id })
          commit("SET_LOADER", { type: "removeData", key: data_id, value: false })
          return { status: promise.status, data: promise.data }
        } catch (error) {
          commit("SET_LOADER", { type: "removeData", key: data_id, value: false })
          let errors = {}
          Object.keys(error.response.data.errors).forEach(e => { errors[e] = error.response.data.errors[e][0] })
          commit("SET_ERRORS", errors)
          return { ...error.response }
        }
      }
    }

    mutations = {
      /**
       * Persists provided codelist in the state
       * Persists full codelist namespaced by schema slug (schema object)
       * Persists codelist data namespaced by schema slug (array of objects)
       * Persists codelist data namespaced by schema slug and data id (object with number key and object value)
       * Persists codelist data namespaced by schema slug and data key (object with string key and object value)
       * Persists codelist schema id to key map
       * Persists codelist schema key to id map
       *
       * @param {number} slug - Slug of the codelist schema
       * @param {number} codeList - Codelist schema with data
       */
      SET_CODELIST_OPTIONS (state, { slug, codeList }) {
        Vue.set(state.codeLists, slug, codeList)
        Vue.set(state.options, slug, codeList.data)
        Vue.set(state.optionsById, slug, {})
        Vue.set(state.optionsByKey, slug, {})
        codeList.data.forEach(value => {
          state.optionsById[slug][value.id] = value
          state.optionsByKey[slug][value.key] = value
        })
        Vue.set(state.schemaIds, slug, codeList.id)
        Vue.set(state.schemaKeys, codeList.id, slug)
      },
      /**
       * Removes one or multiple codelists from the state
       * An id OR slug or aray of those must be provided
       *
       * @param {number|array} id - An id or array of ids of codelists to be removed
       * @param {string|array} slug - A slug or array of slugs of codelists to be removed
       */
      REMOVE_CODELIST_OPTIONS (state, { id, slug }) {
        let removeCodeLists = (id, slug) => {
          Vue.delete(state.optionsByKey, slug)
          Vue.delete(state.optionsById, id)
          Vue.delete(state.codeLists, slug)
          Vue.delete(state.schemaIds, slug, id)
          Vue.delete(state.schemaKeys, id, slug)
        }
        if (id) {
          slug = state.codeLists.find(e => e.id === id)
          if (!Array.isArray(id)) {
            removeCodeLists(id, slug)
          } else {
            id.forEach(item => {
              slug = state.codeLists.find(e => e.id === item)
              removeCodeLists(id, slug)
            })
          }
        } else if (slug) {
          if (!Array.isArray(slug)) {
            id = state.codeLists.find(e => e.slug === slug)
            removeCodeLists(id, slug)
          } else {
            slug.forEach(item => {
              id = state.codeLists.find(e => e.slug === item)
              removeCodeLists(id, slug)
            })
          }
        }
      }
    }
}
